import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import StandardModal from "@/js/components/shared/StandardModalV2";
import "bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import iconNewWindow from "assets/IconNewWindow.svg?url";

import moment from "moment";
import { getBhsScreenshot, getBhsScreenshotZip } from "@/js/api/bhsApi";
import LoadingSpinner from "../LoadingSpinner";
import { useTimeout } from "react-use";
import { isScreenshareUrl } from "@/components/Screenshare/util";

const SCREENSHARE_PAGE_TITLE = "Teacher's shared screen";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const SHViewModal = ({ handleClose, student, activity }) => {
	const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);
	const [currentScreenshot, setCurrentScreenshot] = useState();
	const [loading, setLoading] = useState(false);
	const [zippingState, setZippingState] = useState(false);

	const durationSeconds = Math.floor(moment.duration(activity.end.diff(activity.start)).asSeconds());
	const durationMinutes = Math.floor(durationSeconds / 60);
	const durationHours = Math.floor(durationMinutes / 60);

	const durationString =
		durationHours >= 2
			? durationHours + " hours"
			: durationMinutes >= 2
				? durationMinutes + " minutes"
				: durationMinutes >= 1
					? durationMinutes + " minute"
					: durationSeconds + " seconds";

	const handleNavClick = (e) => {
		const screenshotIndex = 1 * e.target.dataset.screenshotIndex;
		setCurrentScreenshotIndex(screenshotIndex);
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const handleDownloadZip = (e) => {
		setZippingState(true);
		var startTime = activity.start;
		var endTime = activity.end;
		var url = activity.activity;

		getBhsScreenshotZip(student, startTime, endTime, url);

		// Was not able to set up a proper hook for when the zip download finishes because it is a streamed action
		// This is enough though to give the user an indicator that the download is underway
		setTimeout(() => setZippingState(false), 1500);
	};

	useEffect(() => {
		setLoading(true);
		getBhsScreenshot(activity.screenshots[currentScreenshotIndex].screenshot).then((img) => {
			setCurrentScreenshot(img);
			setLoading(false);
		});
	}, [activity.screenshots, currentScreenshotIndex]);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const openInNewWindow = (e) => {
		window.open(currentScreenshot, "_blank");
	};

	const displayActivityLinks = (activity) => {
		const isScreenshare = isScreenshareUrl(activity);
		return isScreenshare ? (
			SCREENSHARE_PAGE_TITLE
		) : (
			<a href={`https://${activity}`} rel="noreferrer" target="_blank">
				{" "}
				{activity}{" "}
			</a>
		);
	};

	return (
		<StandardModal open close={handleClose} size="content" contentClassName="SHModal" rootClassName="SHModal_wrapper" ariaHideApp={false}>
			<div className="SHModal_header">
				<h2>Screenshot Collection of:</h2>
				{student} on <span className="SHModal_linkText">{displayActivityLinks(activity.activity.split(","))}</span> for {durationString}
			</div>
			<div className="SHModal_content">
				<div className="SHModal_leftNavigation">
					{activity.screenshots.map((screenshot, i) => (
						<div
							href="#"
							key={i}
							data-screenshot-index={i}
							className={`${i === currentScreenshotIndex && "active"}`}
							onClick={handleNavClick}
						>
							{moment.utc(screenshot.timestamp).local().format("hh:mm:ss A")}
						</div>
					))}
				</div>

				<div className="SHModal_fullScreen">
					{!loading && (
						<Img
							className="SHModal_imageWindow"
							alt={`${activity.screenshots[currentScreenshotIndex].title} (${activity.screenshots[currentScreenshotIndex].fullUrl})`}
							src={currentScreenshot}
						/>
					)}
					{loading && <LoadingSpinner />}
					<div>
						<button href="#" onClick={openInNewWindow} target="_blank" rel="noopener noreferrer">
							Open in Full Screen
							<img className="SHModal_newWindowIcon" src={iconNewWindow} alt="" />
						</button>
					</div>
				</div>
			</div>
			<div>
				<button
					className={`SHModal_modalButton ${zippingState === true ? "SHModal_modalButtonSpinner" : "SHModal_showExport"}`}
					onClick={handleDownloadZip}
				>
					Download Screenshots.zip
				</button>
			</div>
		</StandardModal>
	);
};

/* This component forcefully re-renders the image after 50ms of the first render, by using key change. On MacOS Chrome this fixes
 * a bug where some images don't properly paint the first time. For cases where this issue isn't occurring, there will be no pixel
 * difference on the re-render, and no 'flashing' should occur. The 50ms delay is needed (or some suitable delay), as a zero (next tick)
 * or 'low' timeout does not fix the issue.
 */
const Img = (props) => {
	const [isReady] = useTimeout(50);

	const key = isReady() ? "1" : "2";
	return <img key={key} {...props} alt="" />;
};

Img.propTypes = {
	className: PropTypes.string.isRequired,
	alt: PropTypes.string.isRequired,
	src: PropTypes.string.isRequired,
};

SHViewModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	student: PropTypes.string,
	activity: PropTypes.object,
	isRestricted: PropTypes.bool,
};

export default SHViewModal;
